import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Bot, ExternalLink, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { AgentLogoImage } from "@/components/agents/AgentLogoImage";
import {
	DashboardContent,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { useListAgentDialog } from "@/components/ListAgentDialog";
import {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogFooter,
	ConfirmDeleteDialogHeader,
} from "@/components/ui/confirm-delete-dialog";
import { toast } from "@/lib/toast";

type MilestoneEvent = {
	id: string;
	agentId: string;
	agentName: string;
	agentSlug: string;
	milestone: number;
	crossedAt: string;
};

type AgentListing = {
	agentId: string;
	name: string;
	slug: string;
	website: string;
	listingStatus: string;
	estimatedMrr: number;
	currency: string;
	activeSubscriptions: number;
	lastVerifiedAt: string | null;
	verificationStatus: string | null;
	verificationProvider: string | null;
	logoUrl: string | null;
	isStealth: boolean;
	description: string | null;
};

export const Route = createFileRoute("/dashboard/")({
	component: DashboardHomePage,
});

function formatMilestone(cents: number): string {
	const dollars = cents / 100;
	if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(0)}M`;
	if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(0)}k`;
	return `$${dollars.toFixed(0)}`;
}

function buildXShareUrl(agentSlug: string, milestone: number): string {
	const milestoneLabel = formatMilestone(milestone);
	const agentUrl = `https://agentmrr.com/agents/${agentSlug}`;
	const text = `Just crossed ${milestoneLabel} MRR 🎉 Verified on @thijs_smudde ${agentUrl}`;
	return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
}

function MilestoneBanner() {
	const queryClient = useQueryClient();

	const { data } = useQuery({
		queryKey: ["milestone-events"],
		queryFn: async () => {
			const res = await fetch("/api/agents/milestone-events");
			if (!res.ok) return { events: [] as MilestoneEvent[] };
			return res.json() as Promise<{ events: MilestoneEvent[] }>;
		},
	});

	const events = data?.events ?? [];
	if (events.length === 0) return null;

	async function dismiss(eventId: string) {
		await fetch("/api/agents/milestone-events", {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ eventId }),
		});
		await queryClient.invalidateQueries({ queryKey: ["milestone-events"] });
	}

	return (
		<div className="mb-4 flex flex-col gap-2">
			{events.map((event) => (
				<div
					key={event.id}
					className="flex items-center justify-between gap-3 border-l-2 border-brand bg-brand-soft px-4 py-3"
				>
					<div className="flex min-w-0 items-center gap-3">
						<span className="font-mono text-sm uppercase tracking-[0.14em] text-brand shrink-0">
							Milestone
						</span>
						<div className="min-w-0">
							<p className="text-base text-foreground">
								<span style={{ fontWeight: 400 }}>{event.agentName}</span> just
								crossed{" "}
								<span className="font-mono text-brand tabular-nums">
									{formatMilestone(event.milestone)} MRR
								</span>
							</p>
							<p className="mt-0.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Verified on AgentMRR
							</p>
						</div>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						<a
							href={buildXShareUrl(event.agentSlug, event.milestone)}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex min-h-8 items-center gap-1.5 bg-brand px-3 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors hover:bg-brand-hover"
						>
							Share on X
						</a>
						<button
							type="button"
							onClick={() => dismiss(event.id)}
							className="p-1.5 text-muted-foreground transition-colors hover:text-brand"
							title="Dismiss"
						>
							<X className="size-3.5" />
						</button>
					</div>
				</div>
			))}
		</div>
	);
}

function formatMrr(mrr: number, currency: string) {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency || "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(mrr / 100);
}

function DashboardHomePage() {
	const { openListAgentDialog } = useListAgentDialog();
	const queryClient = useQueryClient();
	const [deletingAgent, setDeletingAgent] = useState<AgentListing | null>(null);
	const [deleteConfirmName, setDeleteConfirmName] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);

	const handleDeleteConfirm = async () => {
		if (!deletingAgent) return;
		setIsDeleting(true);
		try {
			const res = await fetch("/api/agents/listings", {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ agentId: deletingAgent.agentId }),
			});
			if (!res.ok) {
				const data = (await res.json().catch(() => null)) as {
					error?: string;
				} | null;
				throw new Error(data?.error ?? "Failed to delete agent");
			}
			await queryClient.invalidateQueries({ queryKey: ["agent-listings"] });
			toast.success("Agent deleted", { description: deletingAgent.name });
			setDeletingAgent(null);
			setDeleteConfirmName("");
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to delete agent",
			);
		} finally {
			setIsDeleting(false);
		}
	};

	const { data, isLoading } = useQuery({
		queryFn: async () => {
			const res = await fetch("/api/agents/listings", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch agents");
			return res.json() as Promise<{ agents: AgentListing[] }>;
		},
		queryKey: ["agent-listings"],
	});

	const agents = data?.agents ?? [];

	return (
		<DashboardLayout>
			<DashboardContent className="max-w-3xl">
				<div className="flex items-center justify-between pb-5 mb-4">
					<div>
						<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							&gt; Dashboard / my agents
						</p>
						<p className="mt-2 text-base text-muted-foreground">
							Manage your listed agents and their verification status.
						</p>
					</div>
					<button
						type="button"
						onClick={() => openListAgentDialog("user")}
						className="inline-flex shrink-0 min-h-10 items-center gap-2 bg-brand px-4 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors hover:bg-brand-hover"
					>
						<Plus className="size-3.5" />
						List agent
					</button>
				</div>

				<MilestoneBanner />

				<div>
					{isLoading ? (
						<div className="flex flex-col">
							{[1, 2].map((i) => (
								<div
									key={i}
									className="h-16 animate-pulse border-t border-border bg-muted/30"
								/>
							))}
						</div>
					) : agents.length === 0 ? (
						<button
							type="button"
							onClick={() => openListAgentDialog("user")}
							className="group flex w-full flex-col items-center justify-center border border-border py-16 text-center transition-colors hover:bg-brand-soft"
						>
							<div className="flex size-12 items-center justify-center border border-border bg-background">
								<Bot className="size-5 text-muted-foreground" />
							</div>
							<p className="mt-4 font-mono text-sm uppercase tracking-[0.12em] text-foreground">
								No agents yet
							</p>
							<p className="mt-1 max-w-xs text-base text-muted-foreground">
								List your first agent to showcase verified revenue on the
								leaderboard.
							</p>
							<span className="mt-5 inline-flex min-h-10 items-center gap-2 bg-brand px-4 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors group-hover:bg-brand-hover">
								<Plus className="size-3.5" />
								List your first agent
							</span>
						</button>
					) : (
						<>
							<div className="flex flex-col border-t border-border">
								{agents.map((agent) => (
									<div
										key={agent.agentId}
										className="flex items-center gap-1 border-b border-border transition-colors hover:bg-brand-soft"
									>
										<Link
											to="/dashboard/manage"
											search={{ agentId: agent.agentId }}
											className="flex flex-1 min-w-0 items-center justify-between gap-4 py-3 pl-4 pr-4 sm:pl-5 sm:pr-5"
										>
											<div className="flex min-w-0 items-center gap-3">
												<AgentLogoImage
													logoUrl={agent.logoUrl}
													website={agent.website}
													mode={agent.isStealth ? "stealth" : undefined}
													name={(agent.name || "U")[0].toUpperCase()}
												/>
												<div className="min-w-0">
													<p
														className="text-base text-foreground truncate"
														style={{ fontWeight: 400 }}
													>
														{agent.name || "Untitled agent"}
													</p>
													{agent.description && (
														<p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">
															{agent.description}
														</p>
													)}
												</div>
											</div>
											<div className="flex items-center gap-3 shrink-0">
												{agent.estimatedMrr > 0 && (
													<span className="font-mono text-sm tabular-nums text-foreground">
														{formatMrr(agent.estimatedMrr, agent.currency)}
														<span className="text-muted-foreground">/mo</span>
													</span>
												)}
												<span
													className={`font-mono text-sm uppercase tracking-[0.14em] ${
														agent.listingStatus === "published"
															? "text-brand"
															: agent.listingStatus === "verification_failed"
																? "text-[color:var(--ticker-down)]"
																: "text-muted-foreground"
													}`}
												>
													{agent.listingStatus.replace(/_/g, " ")}
												</span>
											</div>
										</Link>
										<div className="mr-3 flex items-center gap-1 shrink-0">
											{agent.listingStatus === "published" && (
												<Link
													to="/agents/$agentSlug"
													params={{ agentSlug: agent.slug }}
													title="View public page"
													className="p-1.5 text-muted-foreground transition-colors hover:text-brand"
												>
													<ExternalLink className="size-3.5" />
												</Link>
											)}
											<button
												type="button"
												onClick={() => setDeletingAgent(agent)}
												className="p-1.5 text-muted-foreground transition-colors hover:text-[color:var(--ticker-down)]"
												title="Delete agent"
											>
												<Trash2 className="size-3.5" />
											</button>
										</div>
									</div>
								))}
							</div>

							<ConfirmDeleteDialog
								open={deletingAgent !== null}
								onOpenChange={(open) => {
									if (!open) {
										setDeletingAgent(null);
										setDeleteConfirmName("");
									}
								}}
							>
								<ConfirmDeleteDialogContent>
									<ConfirmDeleteDialogHeader
										title="Delete agent"
										description={`This will permanently remove "${deletingAgent?.name || "this agent"}" from your listings. This action cannot be undone.`}
									/>
									<ConfirmDeleteDialogConfirmation
										value={deleteConfirmName}
										onChange={setDeleteConfirmName}
										confirmText={deletingAgent?.name || ""}
									/>
									<ConfirmDeleteDialogFooter
										onCancel={() => {
											setDeletingAgent(null);
											setDeleteConfirmName("");
										}}
										onConfirm={handleDeleteConfirm}
										isLoading={isDeleting}
										confirmLabel="Delete agent"
										confirmDisabled={
											deleteConfirmName !== (deletingAgent?.name || "")
										}
									/>
								</ConfirmDeleteDialogContent>
							</ConfirmDeleteDialog>
						</>
					)}
				</div>
			</DashboardContent>
		</DashboardLayout>
	);
}
